import React, { useContext, useState, useEffect, useRef } from 'react'
import { View, StyleSheet, FlatList, ScrollView, TouchableOpacity } from 'react-native'
import { Text, Button, Card, Icon, Spinner, Modal, Layout, Datepicker, RangeDatepicker, useTheme } from '@ui-kitten/components'
import { format, addDays, getDaysInMonth } from 'date-fns'
import { countWorkingHoursStats } from '../Stats/statHelpers'
import { getStatsForDays } from '../../global/Firebase/firebase'
import { saveWorkingHours, deleteWorkingHours, recalculateUserSalaryAmounts } from '../../global/Firebase/workingHours'
import { UserContext } from '../../global/UserProvider'
import Divider from '../../components/Divider'
import HeaderIconButton from '../../components/HeaderIconButton'
import { WORKING_HOURS } from '../Shift/ShiftScreen.js'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose, getLocalStoreDate, getDailyStatID } from '../../utils/timezoneFunctions'
import { currencyFormatterAtom, localeDateServiceAtom, organizationAtom, rolesDictAtom, storesDictAtom, userRoleAtom, usersDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { countEmployeeBonuses } from '../../global/Firebase/workingHours'
import { rethrowUnlessWRefusal } from '../../global/Write/writeW'
import MoneyTextInput from '../../components/MoneyTextInput'
import { useColors } from '../../utils/useColors'
import { addDecimalAmounts, parseDecimalAmount } from '../../utils/decimalInput'
import { ModalView } from '../../components/ModalView.js'
import { shouldUseSupabase } from '../../global/Firebase/types'
import { transactionsApi, workingHoursApi } from '../../global/API/typedApiClient'
import { readWholeList, newestFirst } from '../../global/API/readWholeList'
import { createRealtimeChannel, unsubscribeChannel } from '../../global/Supabase/supabaseClient'
import { salaryRecordDateLabel } from './salaryRecordDate'

const initialRange = () => {
  var startOfMonth = new Date()
  startOfMonth.setDate(1)
  startOfMonth.setHours(0,0,0,0)
  var endOfMonth = new Date()
  endOfMonth.setDate(getDaysInMonth(endOfMonth))
  return {startDate: startOfMonth, endDate: endOfMonth}
}

export default function SalaryScreen( { navigation, route } ) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { preselectedUser } = route.params
  const db = context.db
  const usersDict = useAtomValue(usersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const rolesDict = useAtomValue(rolesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const organization = useAtomValue(organizationAtom)
  const [range, setRange] = useState(initialRange())
  const [monthSalaryRange, setMonthSalaryRange] = useState(initialRange())
  const [monthSalaryAmount, setMonthSalaryAmount] = useState('')
  const [workingHours, setWorkingHours] = useState()
  const [individualSalesAmount, setIndividualSalesAmount] = useState()
  const [individualReturnsAmount, setIndividualReturnsAmount] = useState()
  const [transactions, setTransactions] = useState(null)
  const [totalPaidValue, setTotalPaidValue] = useState(null)
  const [salesData, setSalesData] = useState({data: [], labels: []})
  const [factSales, setFactSales] = useState(0)
  const [amount, setAmount] = useState(null)
  const [bonusAmount, setBonusAmount] = useState(null)
  const [hoursQuantity, setHoursQuantity] = useState(null)
  const [daysQuantity, setDaysQuantity] = useState(null)
  const [user, setUser] = useState(preselectedUser != null ? usersDict[preselectedUser] : usersDict[context.user.uid])
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [oldValue, setOldValue] = useState(null)
  const [startHour, setStartHour] = useState(null)
  const [endHour, setEndHour] = useState(null)
  const [date, setDate] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [saveError, setSaveError] = useState()
  const _rangePicker = React.createRef()
  const _monthSalaryRangePicker = React.createRef()
  const [refreshing, setRefreshing] = useState(false)
  const [foundErrorModalIsShown, setFoundErrorModalIsShown] = useState(false)
  const [isValidationLoading, setIsValidationLoading] = useState(false)
  // Ref to track if validations have been triggered to prevent duplicate calls
  const bonusesTriggeredRef = useRef(false)

  // Reset the ref when user or range changes to allow recalculation
  useEffect(() => {
    bonusesTriggeredRef.current = false
  }, [user, range])

  // Supabase mode: stable refetch callback for working hours
  const fetchWorkingHoursRef = useRef(null)
  const refetchWorkingHours = () => fetchWorkingHoursRef.current?.()

  useEffect(() => {
    if (user != null && range.startDate != null && range.endDate != null) {
      _rangePicker.current.blur()
      let localDateRange = dateRangeWithTimezoneAndAutoclose(range, [user.store.id])
      getStats(localDateRange)

      let unsubscribeWorkingHours = () => {}

      // ATL-1477: latest wins. Refetches overlap (a mutation, a realtime burst, a channel rejoin)
      // and can finish out of order; a slower, OLDER read must not overwrite a newer one, and no
      // read may land after this range/user was left. Each fetch takes a ticket; only the newest
      // ticket of a live effect may set state.
      let disposed = false
      let workingHoursTicket = 0
      let transactionsTicket = 0

      if (shouldUseSupabase()) {
        // Supabase mode: fetch via API, refetch after mutations
        const fetchWorkingHours = async () => {
          const ticket = ++workingHoursTicket
          try {
            // ATL-1477: every entry in the range, proven complete; a single `limit: 1000` request cut off the rest.
            const data = await readWholeList(
              (window) => workingHoursApi.getWorkingHoursWindow({
                employeeId: user.uid,
                startDate: localDateRange.startDate.toISOString(),
                endDate: localDateRange.endDate.toISOString(),
                ...window,
              }),
              { label: 'working-hours', order: newestFirst('date') },
            )
            if (disposed || ticket !== workingHoursTicket) return
            // API client already deserializes date fields to TimestampLike via DATE_FIELDS.
            // Just add ref for Firestore-compatible shape.
            const docs = data.map(item => ({
              ...item,
              ref: { id: item.id },
            }))
            const [newAmount, newHoursQuantity, newDaysQuantity, newBonusAmount] = countWorkingHoursStats(docs, rolesDict)
            setAmount(newAmount)
            setBonusAmount(newBonusAmount)
            setHoursQuantity(newHoursQuantity)
            setDaysQuantity(newDaysQuantity)
            setWorkingHours(docs)

            if (!bonusesTriggeredRef.current) {
              bonusesTriggeredRef.current = true
              const screenName = route?.name || navigation?.getState?.()?.routes?.[navigation?.getState?.()?.index]?.name || 'SalaryScreen'
              countEmployeeBonuses(docs, localDateRange, user.store.id, user.id, null, false, screenName).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
            }
            recalculateUserSalaryAmounts(user.uid).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
          } catch (error) {
            if (disposed || ticket !== workingHoursTicket) return
            console.error('Error fetching working hours from Supabase:', error)
            Sentry.captureException(error)
          }
        }
        fetchWorkingHoursRef.current = fetchWorkingHours
        fetchWorkingHours()
      } else {
        // Firestore mode: real-time listener (legacy)
        const unsub = db.collection('workingHours').where('employeeID', '==', user.uid).where('date', '>=', localDateRange.startDate).where('date', '<=', localDateRange.endDate).orderBy('date', 'desc').onSnapshot(function(querySnapshot) {
          var docs = []
          querySnapshot.forEach(function(doc) {
            var snap = doc.data()
            snap.id = doc.id
            snap.ref = doc.ref
            docs.push(snap)
          })
          const [newAmount, newHoursQuantity, newDaysQuantity, newBonusAmount] = countWorkingHoursStats(docs, rolesDict)
          setAmount(newAmount)
          setBonusAmount(newBonusAmount)
          setHoursQuantity(newHoursQuantity)
          setDaysQuantity(newDaysQuantity)
          setWorkingHours(docs)

          if (!bonusesTriggeredRef.current) {
            bonusesTriggeredRef.current = true
            const screenName = route?.name || navigation?.getState?.()?.routes?.[navigation?.getState?.()?.index]?.name || 'SalaryScreen'
            countEmployeeBonuses(docs, localDateRange, user.store.id, user.id, null, false, screenName).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
          }
          recalculateUserSalaryAmounts(user.uid).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
        })
        unsubscribeWorkingHours = unsub
      }

      let unsubscribeTransactions = () => {}
      if (shouldUseSupabase()) {
        // Supabase mode: fetch via API with realtime subscription
        const fetchTransactions = async () => {
          const ticket = ++transactionsTicket
          try {
            // ATL-1477: this employee's expenses in the range, filtered by the backend
            // (`externalDestinationId`, ATL-986 — matches `externalDestination.id`, the same field
            // the Firestore branch queries, in EVERY stored form of this user: the Firebase uid the
            // dual-write writes and the Supabase UUID the backend writes — `counterpartyIdAliases`)
            // and proven complete. It used to page EVERY expense of the organization and filter
            // client-side, capped at `limit: 1000`.
            const userTxns = await readWholeList(
              (window) => transactionsApi.getTransactionsWindow({
                type: 'expense',
                externalDestinationId: user.uid,
                startDate: localDateRange.startDate.toISOString(),
                endDate: localDateRange.endDate.toISOString(),
                ...window,
              }),
              { label: 'transactions', order: newestFirst('date') },
            )
            if (disposed || ticket !== transactionsTicket) return
            const paidValue = userTxns.reduce((sum, t) => sum + (t.value || 0), 0)
            setTotalPaidValue(paidValue)
            setTransactions(userTxns)
          } catch (error) {
            if (disposed || ticket !== transactionsTicket) return
            console.error('Error fetching salary transactions:', error)
            Sentry.captureException(error)
          }
        }
        fetchTransactions()

        // Set up realtime subscription for live updates
        const supabaseOrgId = organization?.supabaseId
        let channel = null
        if (supabaseOrgId) {
          let debounceTimer = null
          channel = createRealtimeChannel(
            `salary_transactions:${supabaseOrgId}`,
            'transactions',
            supabaseOrgId,
            (payload) => {
              // Only refetch for expense transactions (skip sales, supplies, transfers, etc.)
              const record = payload.new || payload.old || {}
              if (record.type && record.type !== 'expense') return
              if (debounceTimer) clearTimeout(debounceTimer)
              debounceTimer = setTimeout(fetchTransactions, 300)
            },
            // ATL-1405: a rejoin replays nothing that changed while the channel was down.
            { onStateChange: (state, gap) => { if (state === 'subscribed' && gap) void fetchTransactions() } }
          )
          unsubscribeTransactions = () => {
            if (debounceTimer) clearTimeout(debounceTimer)
            unsubscribeChannel(channel)
          }
        }
      } else {
        // Firestore mode: real-time listener
        unsubscribeTransactions = db.collection('transactions').where('externalDestination.id', '==', user.uid).where('date', '>=', localDateRange.startDate).where('date', '<=', localDateRange.endDate).orderBy('date', 'desc').onSnapshot(function(querySnapshot) {
          var docs = []
          var paidValue = 0
          querySnapshot.forEach(function(doc) {
            var snap = doc.data()
            snap.id = doc.id
            snap.ref = doc.ref
            paidValue += snap.value
            docs.push(snap)
          })
          setTotalPaidValue(paidValue)
          setTransactions(docs)
        })
      }

      if (user.salaryCreditedAmount == null || user.salaryPaidAmount == null) {
        handleRefreshAllTimeSalaryPaidCredited()
      }

      return () => {
        disposed = true
        unsubscribeWorkingHours()
        unsubscribeTransactions()
      }
    }
  }, [user, range])

  useEffect(() => {
    if (user != null) {
      setUser(usersDict[user.uid])
    }
  }, [usersDict])

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [])
  
  useEffect(() => {
    setUser(preselectedUser != null ? usersDict[preselectedUser] : usersDict[context.user.uid])
  }, [Object.keys(usersDict).length, context.user.uid])

  const addHourlyEmployeeToShift = async () => {
    setSaveError(null)
    if (date != null && startHour != null && endHour != null && startHour.value < endHour.value) {
      setIsLoading(true)
      try {
        await saveWorkingHours(oldValue, {
          employeeID: user?.uid,
          role: user?.role,
          roleDetails: rolesDict?.[user?.role],
          salaryType: 'hour',
          startHour: startHour,
          endHour: endHour,
          amount: ((endHour.value-startHour.value) * rolesDict?.[user?.role].salary),
          date: date,
          dailyStatID: getDailyStatID(user?.store?.id, date),
          store: user?.store?.id,
        })
        refetchWorkingHours()
        setAddModalVisible(false)
      } catch(error) {
        setSaveError(error.message)
        Sentry.captureException(error, { tags: { books_write_error: 'working-hours-hourly-save' } })
      }
      setIsLoading(false)
    }
  }

  const addDayEmployeeToShift = async () => {
    setSaveError(null)
    if (date != null) {
      setIsLoading(true)
      try {
        await saveWorkingHours(oldValue, {
          employeeID: user?.uid,
          role: user?.role,
          roleDetails: rolesDict?.[user?.role],
          salaryType: 'day',
          amount: rolesDict?.[user?.role].salary,
          date: date,
          dailyStatID: getDailyStatID(user?.store?.id, date),
          store: user?.store?.id,
        })
        refetchWorkingHours()
        setAddModalVisible(false)
      } catch(error) {
        Sentry.captureException(error, { tags: { books_write_error: 'working-hours-day-save' } })
        setSaveError(error.message)
      }
      setIsLoading(false)
    }
  }

  const addMonthEmployeeWorkingHours = async () => {
    setSaveError(null)
    // ATL-1371 — a salary PAYMENT amount. The keypad here is `numeric`, which is already
    // `UIKeyboardTypeDecimalPad` on iOS, so a decimal could always be TYPED — but its separator
    // follows the device locale, and `parseFloat('36,5')` is 36. The box was readable and the
    // number it produced was wrong, which is the half of ATL-1371 no screenshot shows.
    const floatMonthSalaryAmount = parseDecimalAmount(monthSalaryAmount)
    if (monthSalaryRange.startDate.getMonth() !== monthSalaryRange.endDate.getMonth()) { // to simpify forming profit stats by month 
      setSaveError(t('salary.chooseSameMonthError'))
      return
    }
    if (monthSalaryRange.startDate == null || monthSalaryRange.endDate == null || floatMonthSalaryAmount === null) {
      setSaveError(t('notAllFieldsFilled'))
      return
    }
    setIsLoading(true)
    try {
      await saveWorkingHours(oldValue, {
        employeeID: user?.uid,
        role: user?.role,
        roleDetails: rolesDict?.[user?.role],
        salaryType: 'month',
        amount: floatMonthSalaryAmount,
        date: monthSalaryRange.startDate,
        startDate: getLocalStoreDate(user?.store?.id, monthSalaryRange.startDate),
        endDate: getLocalStoreDate(user?.store?.id, monthSalaryRange.endDate),
        // ATL-1954: the record's own date, not the hour/day picker's state (null on this path,
        // which produced the epoch id `1-1-1970-<store>`).
        dailyStatID: getDailyStatID(user?.store?.id, monthSalaryRange.startDate),
        store: user?.store?.id,
      })
      refetchWorkingHours()
      setAddModalVisible(false)
    } catch(error) {
      setSaveError(error.message)
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'working-hours-month-salary-save' } })
    }
    setIsLoading(false)
  }

  const deleteWorkingHour = async () => {
    setIsLoading(true)
    try {
      await deleteWorkingHours(oldValue)
      refetchWorkingHours()
    } catch(error) {
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'working-hours-delete' } })
    }
    setMonthSalaryRange(range)
    setIsLoading(false)
    setAddModalVisible(false)
  }

  const getStats = async (range) => {
    var sales = 0
    const stats = await getStatsForDays(db, range,  user?.store?.id, false)
    var newSalesData = {data: [], labels: []}
    var date = range.startDate
    var newIndividualSalesAmount = 0
    var newIndividualReturnsAmount = 0
    for (const [index, value] of stats.entries()) {
      sales += (value.saleAmount || 0)
      newSalesData.data.push((value.saleAmount || 0)/1000)
      newSalesData.labels.push(format(getLocalStoreDate(user.store.id, date), 'd/M/y E'))
      date = addDays(date, 1)
      newIndividualSalesAmount += (value[user.id + 'SalesAmount'] || 0)
      newIndividualReturnsAmount += (value[user.id + 'ReturnsAmount'] || 0)
    }
    setSalesData(newSalesData)
    setFactSales(sales)
    setIndividualSalesAmount(newIndividualSalesAmount)
    setIndividualReturnsAmount(newIndividualReturnsAmount)
  }
  
  const renderItem = ({item, index}) => {
    // ATL-1954: a monthly record without its period used to throw here and white-screen the app.
    const date = salaryRecordDateLabel(item, {
      formatDate: (instant) => format(getLocalStoreDate(user.store.id, instant), "d/M/y EEEE"),
      missingLabel: t('salary.periodMissing'),
    })
    return (
      <Card
        style={styles.item}
        onPress={() => {
          if (userRole?.permissions?.['changeOthersWorkingHours'] || context.user.role === 'admin') {
            setOldValue(item)
            setDate(item.date.toDate())
            setStartHour(item.startHour)
            setEndHour(item.endHour)
            setMonthSalaryRange(item.monthSalaryRange)
            setMonthSalaryAmount(item.amount)
            setAddModalVisible(true)
          }
        }}
      >
        <View style={styles.dateTimeView}>
          <Icon name='calendar-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {date}
          </Text>
        </View>
        { item.salaryType === 'hour' && (
          <View style={styles.dateTimeView}>
            <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
            <Text
              style={styles.itemTitle}
              category='s2'>
              {t('salary.fromToHours', {start: item.startHour.text, end: item.endHour.text})}
            </Text>
          </View>
        )}
        <View style={styles.dateTimeView}>
          <Icon name='pricetags-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {currencyFormatter.format(item.amount)}
          </Text>
        </View>
        { item.employeeBonusAmount != null && (
          <View style={styles.dateTimeView}>
            <Icon name='gift-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
            <Text
              style={styles.itemTitle}
              category='s2'>
              {(currencyFormatter.format(item.employeeBonusAmount) || '0')}
            </Text>
          </View>
        )}
      </Card>
    )
  }

  const renderTransactionItem = ({item, index}) => {
    const dateValue = item.date?.toDate ? item.date.toDate() : new Date(item.date)
    const date = format(getLocalStoreDate(user.store.id, dateValue), "d/M/y EEEE")
    return (
      <Card
        style={styles.item}
        onPress={() => {
          navigation.navigate('PaymentTransactionDetail', {detail: item})
        }}
      >
        <View style={styles.dateTimeView}>
          <Icon name='calendar-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {date}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          <Icon name='pricetags-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {currencyFormatter.format(item.value)}
          </Text>
        </View>
      </Card>
    )
  }

  const handleRefreshAllTimeSalaryPaidCredited = async () => {
    setRefreshing(true)
    try {
      await recalculateUserSalaryAmounts(user.uid)
    } catch (e) {
      console.log(e)
      Sentry.captureException(e, { tags: { books_write_error: 'user-salary-amounts-recalculate' } })
    }
    setRefreshing(false)
  }

  return (
    <ScrollView style={styles.container}>

      <Text category='h4' style={styles.header}>{user?.name}</Text>

      <Text category='label' style={{marginBottom: 5, marginTop: 10, marginHorizontal: 20}}>
        {t('filterByDate')}
      </Text>
      <RangeDatepicker
        placement='top'
        min={new Date('2020-12-01')}
        max={new Date('2029-12-31')}
        range={range}
        ref={_rangePicker}
        style={{marginHorizontal: 20}}
        dateService={localeDateService}
        onSelect={(newRange) => {
          if (newRange.endDate) {
            setRange(newRange)
          } else if (newRange.startDate && range.startDate === newRange.startDate) {
            let endDate = new Date(newRange.startDate)
            endDate.setHours('23', '59', '59', '99')
            setRange({startDate: newRange.startDate, endDate})
          } else {
            setRange(newRange)
          }
        }}
      />

      {(userRole?.permissions?.['viewOthersWorkingHours'] || context.user.role === 'admin') && (
        <>
          <Text category='label' style={{marginBottom: 5, marginTop: 10, marginHorizontal: 20}}>
            {t('salary.employee')}
          </Text>
          <PlatformDependantSelect
            data={Object.values(usersDict)}
            selectedOption={user}
            onSelect={setUser}
            style={{marginHorizontal: 20, marginBottom: 10}}
          />
        </>
      )}
      <Text category='label' style={styles.header}>{t('store') + ': ' + storesDict[user?.store?.id]?.name}</Text>
      <Text category='label' style={styles.header}>{t('role') + ': ' + rolesDict[user?.role]?.name}</Text>
      <Text category='label' style={styles.header}>{t('salary.salaryType') + ': ' + t('roles.' + rolesDict[user?.role]?.salaryType)}</Text>
      <Text category='label' style={styles.header}>{t('salary.bonusType') + ': ' + (rolesDict[user?.role]?.roleBonusType ? t('roles.' + rolesDict[user?.role]?.roleBonusType) : t('salary.withoutBonuses'))}</Text>
      { rolesDict[user?.role]?.salaryType === 'hour' && (<Text category='label' style={styles.header}>{t('salary.hoursQuantity') + ': ' + hoursQuantity}</Text>)}
      { rolesDict[user?.role]?.salaryType === 'day' && (<Text category='label' style={styles.header}>{t('salary.daysQuantity') + ': ' + daysQuantity}</Text>)}
      <Text category='label' style={styles.header}>{t('salary.monthlySales') + ': ' + currencyFormatter.format(individualSalesAmount - (individualReturnsAmount || 0))}</Text>
      <Text category='label' style={styles.header}>{t('salary.salaryAccrued') + ': ' + currencyFormatter.format(amount)}</Text>
      <Text category='label' style={styles.header}>{t('salary.salaryPaid') + ': ' + currencyFormatter.format(totalPaidValue)}</Text>
      { (salesData.data.length > 0 && rolesDict?.[user?.role].roleBonusType === 'monthlyPlan' && storesDict?.[user?.store?.id].monthlyPlan != null) && (
        <View style={{marginBottom: 20, marginHorizontal: 20}}>
          <Text category='h5' style={{marginBottom: 10}}>{t('salary.salesPlan') + ' ' + storesDict?.[user?.store?.id]?.name}</Text>
          <Text>{t('salary.baseline') + ': ' + currencyFormatter.format(storesDict?.[user?.store?.id]?.monthlyPlan) + ' (' + currencyFormatter.format(storesDict?.[user?.store?.id].monthlyPlan/getDaysInMonth(new Date())) + ' ' + t('salary.daily') + ')'}</Text>
          <Text>{'10% ' + t('salary.bonus') + ': ' + currencyFormatter.format(storesDict?.[user?.store?.id]?.monthlyPlan * 1.1) + ' (' + currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.1)/getDaysInMonth(new Date())) + ' ' + t('salary.daily') + ')'}</Text>
          <Text>{'20% ' + t('salary.bonus') + ': ' + currencyFormatter.format(storesDict?.[user?.store?.id]?.monthlyPlan * 1.2) + ' (' + currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.2)/getDaysInMonth(new Date())) + ' ' + t('salary.daily') + ')'}</Text>
          <Text>{'30% ' + t('salary.bonus') + ': ' + currencyFormatter.format(storesDict?.[user?.store?.id]?.monthlyPlan * 1.3) + ' (' + currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.3)/getDaysInMonth(new Date())) + ' ' + t('salary.daily') + ')'}</Text>
          <Text>{'40% ' + t('salary.bonus') + ': ' + currencyFormatter.format(storesDict?.[user?.store?.id]?.monthlyPlan * 1.4) + ' (' + currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.4)/getDaysInMonth(new Date())) + ' ' + t('salary.daily') + ')'}</Text>
          <Text>{'50% ' + t('salary.bonus') + ': ' + currencyFormatter.format(storesDict?.[user?.store?.id]?.monthlyPlan * 1.5) + ' (' + currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.5)/getDaysInMonth(new Date())) + ' ' + t('salary.daily') + ')'}</Text>
          <Divider />
          <Text>{t('salary.factSales', {count: salesData.labels.length}) + ': ' + currencyFormatter.format(factSales)}</Text>
          <Text>{t('salary.tillBaseline') + ': ' + (storesDict?.[user?.store?.id]?.monthlyPlan > factSales ? currencyFormatter.format(storesDict?.[user?.store?.id].monthlyPlan-factSales) + ' (' + currencyFormatter.format((storesDict?.[user?.store?.id].monthlyPlan-factSales)/(getDaysInMonth(new Date())-salesData.labels.length)) + ' ' + t('salary.daily') + ')' : '✅')}</Text>
          <Text>{t('salary.until') + ' 10% ' + t('salary.bonus') + ': ' + ((storesDict?.[user?.store?.id]?.monthlyPlan * 1.1) > factSales ? currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.1)-factSales) + ' (' + currencyFormatter.format(((storesDict?.[user?.store?.id]?.monthlyPlan * 1.1)-factSales)/(getDaysInMonth(new Date())-salesData?.labels?.length)) + ' ' + t('salary.daily') + ')' : '✅')} <Text style={{color: '#25D366'}}>{' (~' + currencyFormatter.format(amount*0.1) + ' ' + t('salary.bonus') + ')'}</Text></Text>
          <Text>{t('salary.until') + ' 20% ' + t('salary.bonus') + ': ' + ((storesDict?.[user?.store?.id]?.monthlyPlan * 1.2) > factSales ? currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.2)-factSales) + ' (' + currencyFormatter.format(((storesDict?.[user?.store?.id]?.monthlyPlan * 1.2)-factSales)/(getDaysInMonth(new Date())-salesData?.labels?.length)) + ' ' + t('salary.daily') + ')' : '✅')} <Text style={{color: '#25D366'}}>{' (~' + currencyFormatter.format(amount*0.2) + ' ' + t('salary.bonus') + ')'}</Text></Text>
          <Text>{t('salary.until') + ' 30% ' + t('salary.bonus') + ': ' + ((storesDict?.[user?.store?.id]?.monthlyPlan * 1.3) > factSales ? currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.3)-factSales) + ' (' + currencyFormatter.format(((storesDict?.[user?.store?.id]?.monthlyPlan * 1.3)-factSales)/(getDaysInMonth(new Date())-salesData?.labels?.length)) + ' ' + t('salary.daily') + ')' : '✅')} <Text style={{color: '#25D366'}}>{' (~' + currencyFormatter.format(amount*0.3) + ' ' + t('salary.bonus') + ')'}</Text></Text>
          <Text>{t('salary.until') + ' 40% ' + t('salary.bonus') + ': ' + ((storesDict?.[user?.store?.id]?.monthlyPlan * 1.4) > factSales ? currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.4)-factSales) + ' (' + currencyFormatter.format(((storesDict?.[user?.store?.id]?.monthlyPlan * 1.4)-factSales)/(getDaysInMonth(new Date())-salesData?.labels?.length)) + ' ' + t('salary.daily') + ')' : '✅')} <Text style={{color: '#25D366'}}>{' (~' + currencyFormatter.format(amount*0.4) + ' ' + t('salary.bonus') + ')'}</Text></Text>
          <Text>{t('salary.until') + ' 50% ' + t('salary.bonus') + ': ' + ((storesDict?.[user?.store?.id]?.monthlyPlan * 1.5) > factSales ? currencyFormatter.format((storesDict?.[user?.store?.id]?.monthlyPlan * 1.5)-factSales) + ' (' + currencyFormatter.format(((storesDict?.[user?.store?.id]?.monthlyPlan * 1.5)-factSales)/(getDaysInMonth(new Date())-salesData?.labels?.length)) + ' ' + t('salary.daily') + ')' : '✅')} <Text style={{color: '#25D366'}}>{' (~' + currencyFormatter.format(amount*0.5) + ' ' + t('salary.bonus') + ')'}</Text></Text>
        </View>
      )}
      { salesData.data.length > 0 && ((rolesDict?.[user?.role].roleBonusType === 'dailyPercent' && storesDict?.[user?.store?.id]?.dailyPercent != null) || (rolesDict?.[user?.role].roleBonusType === 'individualSalesPercent' && rolesDict?.[user?.role].individualSalesPercent != null)) && (
        <Text category='label' style={styles.header}>{t('salary.bonuses') + ': ' + currencyFormatter.format(bonusAmount)}</Text>   
      )}
      <Text category='label' style={styles.header}>{t('salary.currentPeriodBalance') + ': ' + currencyFormatter.format(totalPaidValue - amount - bonusAmount)}</Text>
      <Text category='label' style={styles.header}>{t('salary.allTimeBalance') + ': ' + currencyFormatter.format(user?.salaryPaidAmount - user?.salaryCreditedAmount)}</Text>

      <View style={{marginHorizontal: 20, flexDirection: 'row', alignItems: 'center'}}>
        <Text category='h5' style={{}}>{t('salary.workingHours')}</Text>
        { (userRole?.permissions?.['changeOthersWorkingHours'] || context.user.role === 'admin') && (
          <View testID="wh-add">
            <Button style={{}} appearance='ghost' status='primary' accessoryLeft={(style) => (
              <Icon {...style} name='plus'/>
            )}
            onPress={() => {
              setOldValue(null)
              setMonthSalaryAmount(rolesDict?.[user?.role]?.salary)
              setMonthSalaryRange(range)
              setAddModalVisible(true)
            }}/>
          </View>
        )}
      </View>

      <Modal backdropStyle={styles.backdrop} visible={addModalVisible}>
        <Layout style={styles.modalContainer}>
          <Text appearance='hint' style={{textAlign: 'center', fontSize: 10}}>{oldValue?.id}</Text>
          <Text category='label' style={styles.label}>{t('salary.salary')}</Text>
          { oldValue?.salaryType === 'month' && <Text style={{marginBottom: 10}}>{salaryRecordDateLabel(oldValue, { formatDate: (instant) => format(instant, "d/M/y EEEE"), missingLabel: t('salary.periodMissing') })}</Text>}
          { (oldValue?.salaryType === 'day' || oldValue?.salaryType === 'hour') && <Text style={{marginBottom: 10}}>{format(date, "d/M/y EEEE")}</Text>}
          { oldValue?.salaryType && <Text style={{marginBottom: 10}}>{currencyFormatter.format(oldValue.amount)}</Text>}
          { (!oldValue?.salaryType) && (
            <View>
              { rolesDict?.[user?.role]?.salaryType !== 'month' && (
                <View testID="wh-date">
                  <Datepicker
                    placement='top'
                    date={date}
                    dateService={localeDateService}
                    onSelect={setDate}
                    min={new Date('2020-12-01')}
                    max={new Date('2029-12-31')}
                    style={{alignSelf: 'stretch', marginBottom: 5}}
                  />
                </View>
              )}
              { rolesDict?.[user?.role]?.salaryType === 'month' && (
                <>
                  <Text category='label' style={styles.label}>{t('date')}</Text>
                  <RangeDatepicker 
                    placement='top'
                    min={new Date('2020-12-01')}
                    max={new Date('2029-12-31')}
                    range={monthSalaryRange}
                    ref={_monthSalaryRangePicker}
                    style={{alignSelf: 'stretch', marginBottom: 5}}
                    dateService={localeDateService}
                    onSelect={(newRange) => {
                      if (newRange.endDate) {
                        setMonthSalaryRange(newRange)
                      } else if (newRange.startDate && range.startDate === newRange.startDate) {
                        let endDate = new Date(newRange.startDate)
                        endDate.setHours('23', '59', '59', '99')
                        setMonthSalaryRange({startDate: newRange.startDate, endDate})
                      } else {
                        setMonthSalaryRange(newRange)
                      }
                    }}
                  />
                  <Text category='label' style={styles.label}>{t('salary.amount')}</Text>
                  <MoneyTextInput
                    value={monthSalaryAmount}
                    onChangeText={setMonthSalaryAmount}
                    style={{marginBottom: 10}}
                    placeholder={t('salary.amount')}
                  />
                </>
              )}
              { rolesDict?.[user?.role]?.salaryType === 'hour' && (
                <>
                  <View testID="wh-start-hour">
                    <PlatformDependantSelect
                      data={WORKING_HOURS}
                      selectedOption={startHour}
                      onSelect={(itemValue, itemIndex) => {
                        setStartHour(itemValue)
                      }}
                      style={{marginBottom: 10}}
                    />
                  </View>
                  <View testID="wh-end-hour">
                    <PlatformDependantSelect
                      data={WORKING_HOURS}
                      selectedOption={endHour}
                      onSelect={(itemValue, itemIndex) => {
                        setEndHour(itemValue)
                      }}
                    />
                  </View>
                </>
              )}
            </View>
          ) }
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <>
              <Button style={{marginTop: 10}}appearance='ghost' status='basic' onPress={() => setAddModalVisible(false)}>{t('cancel')}</Button>
              <Button appearance='ghost' status='danger' onPress={deleteWorkingHour}>{t('delete')}</Button>
              { (!oldValue || rolesDict?.[user?.role]?.salaryType === 'hour') && <View testID="wh-save"><Button appearance='ghost' onPress={() => {
                if (rolesDict?.[user?.role]?.salaryType === 'hour') {
                  addHourlyEmployeeToShift()
                } else if (rolesDict?.[user?.role]?.salaryType === 'day') {
                  addDayEmployeeToShift()
                } else if (rolesDict?.[user?.role]?.salaryType === 'month') {
                  addMonthEmployeeWorkingHours()
                }
              }}>{t('save')}</Button></View>}
            </>
          )}
          {saveError && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        </Layout>
      </Modal>
      
      {workingHours != null ? (workingHours.length ? (
        <FlatList 
          data={workingHours} 
          renderItem={renderItem}
          keyExtractor={(item) => item.id} 
          style={styles.list} 
        />
      ) : (
        <Text appearance='hint' style={styles.noItemsText}>
          {t('salary.noDataForMonth')}
        </Text>
      )) : (
        <View style={{alignItems: 'center'}}><Spinner /></View>
      )}

      <View style={{marginHorizontal: 20, flexDirection: 'row', alignItems: 'center'}}>
        <Text category='h5' style={{}}>{t('salary.payouts')}</Text>
        { (userRole?.permissions?.['changeOthersWorkingHours'] || context.user.role === 'admin') && (
          <Button style={{}} appearance='ghost' status='primary' accessoryLeft={(style) => (
            <Icon {...style} name='plus'/>
          )}
          onPress={() => {
            navigation.navigate('PaymentTransactionDetail', {newPaymentDetail: {
              // ATL-1371 — rounded for the same reason as the PaymentStats shortcuts: all three terms are
              // float accumulations, and a residual of 1e-16 clears the form's "more than 0" guard while
              // every number on screen reads 0.
              linkedDocType: 'workingHour', externalDestination: user, value: addDecimalAmounts(addDecimalAmounts(amount, bonusAmount), -totalPaidValue), initialStore: user?.store?.id, paymentRange: range
            }})
          }}/>
        )}
      </View>
      {transactions != null ? (transactions.length ? (
        <FlatList 
          data={transactions} 
          renderItem={renderTransactionItem}
          keyExtractor={(item) => item.id} 
          style={styles.list} 
        />
      ) : (
        <Text appearance='hint' style={styles.noItemsText}>
          {t('salary.noDataForMonth')}
        </Text>
      )) : (
        <View style={{alignItems: 'center'}}><Spinner /></View>
      )}

      <ModalView isVisible={foundErrorModalIsShown} setIsVisible={setFoundErrorModalIsShown}>
        <Text style={{marginBottom: 5}} category="h6">{t('stats.foundError')}</Text>
        <Text style={{marginBottom: 10}}>{t('salary.foundErrorDescription')}</Text>        
        { isValidationLoading 
          ? 
            <View style={{alignItems: 'center'}}><Spinner /></View>
          :
            <Button onPress={async () => {
              setIsValidationLoading(true)
              await recalculateUserSalaryAmounts(user.uid)
              setIsValidationLoading(false)
            }}>{t('stats.recalculate')}</Button>
        }
      </ModalView>
      <Button style={{marginTop: 20}} appearance="ghost" status='basic' onPress={() => setFoundErrorModalIsShown(true)}>{t('stats.foundError')}</Button>

      <View style={{height: 60}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  }, 
  header: {
    marginHorizontal: 20,
    marginVertical: 10,
  },
  list: {
    marginTop: 10,
    marginBottom: 20,
    paddingHorizontal: 20,
  },
  noItemsText: {
    marginTop: 20,
    textAlign: 'center'
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  modalContainer: {
    justifyContent: 'center',
    alignItems: 'stretch',
    width: 320,
    padding: 16,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  label: {
    marginTop: 15,
    marginBottom: 5,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  }
})
